'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ROASTERS, ROASTS, COUNTRY_RANKINGS, WEEKLY_LEADERS, CATEGORIES, POINTS_PER_VOTE } from '@/lib/roastData';
import type { Roast } from '@/lib/roastData';
import { Trophy, Flame, TrendingUp, Globe, Star, Zap, ChevronRight, ThumbsUp, ThumbsDown, Share2, Crown, Users, Plus, X, Check } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import RoastRankAuthNav from './components/RoastRankAuthNav';

function Avatar({ initials, color, size = 40 }: { initials: string; color: string; size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold text-white flex-shrink-0"
      style={{ width: size, height: size, background: color, fontSize: size * 0.35 }}
    >
      {initials}
    </div>
  );
}

function RoastCard({ roast, featured }: { roast: Roast; featured?: boolean }) {
  const [upvotes, setUpvotes] = useState(roast.upvotes);
  const [downvotes, setDownvotes] = useState(roast.downvotes);
  const [voted, setVoted] = useState<'up' | 'down' | null>(null);
  const [pointsFlash, setPointsFlash] = useState(false);
  const [voteAnim, setVoteAnim] = useState<'up' | 'down' | null>(null);

  const handleVote = (dir: 'up' | 'down') => {
    if (voted === dir) {
      setVoted(null);
      if (dir === 'up') setUpvotes(roast.upvotes);
      else setDownvotes(roast.downvotes);
      return;
    }
    const wasUp = voted === 'up';
    const wasDown = voted === 'down';
    setVoted(dir);
    setVoteAnim(dir);
    setTimeout(() => setVoteAnim(null), 600);
    if (dir === 'up') {
      setUpvotes(v => v + 1);
      if (wasDown) setDownvotes(v => v - 1);
      setPointsFlash(true);
      setTimeout(() => setPointsFlash(false), 1800);
    } else {
      setDownvotes(v => v + 1);
      if (wasUp) setUpvotes(v => v - 1);
    }
  };

  return (
    <div className={`card-glass p-5 rounded-2xl transition-all duration-300 hover:scale-[1.01] ${featured ? 'card-glow-green' : ''}`}
      style={{ border: featured ? '1px solid rgba(34,197,94,0.3)' : undefined }}>
      {featured && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', color: 'var(--primary)', border: '1px solid rgba(34,197,94,0.3)' }}>
            🔥 ROAST OF THE DAY
          </span>
        </div>
      )}
      <div className="flex items-start gap-3 mb-3">
        <Avatar initials={roast.initials} color={roast.avatarColor} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm" style={{ color: 'var(--foreground)' }}>{roast.displayName}</span>
            <span className="text-lg" title={roast.country}>{roast.countryFlag}</span>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{roast.country}</span>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(34,197,94,0.1)', color: 'var(--primary)' }}>{roast.category}</span>
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{roast.username} · {roast.submittedAt}</div>
        </div>
      </div>
      <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--foreground)', fontStyle: 'italic' }}>
        "{roast.text}"
      </p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => handleVote('up')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: voted === 'up' ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.05)',
                color: voted === 'up' ? 'var(--primary)' : 'var(--muted-foreground)',
                transform: voteAnim === 'up' ? 'scale(1.2)' : undefined,
                boxShadow: voted === 'up' ? '0 0 12px rgba(34,197,94,0.3)' : undefined,
              }}>
              <ThumbsUp size={14} />
              <span className="text-xs font-bold">{upvotes.toLocaleString()}</span>
            </button>
            {pointsFlash && (
              <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-xs font-bold whitespace-nowrap px-2 py-1 rounded-full animate-bounce"
                style={{ color: '#fff', background: 'rgba(34,197,94,0.9)', boxShadow: '0 0 10px rgba(34,197,94,0.5)' }}>
                +{POINTS_PER_VOTE} pts! 🔥
              </span>
            )}
          </div>
          <button
            onClick={() => handleVote('down')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              background: voted === 'down' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)',
              color: voted === 'down' ? 'var(--danger)' : 'var(--muted-foreground)',
              transform: voteAnim === 'down' ? 'scale(1.2)' : undefined,
            }}>
            <ThumbsDown size={14} />
            <span className="text-xs font-bold">{downvotes.toLocaleString()}</span>
          </button>
          {voted && (
            <span className="text-xs font-semibold px-2 py-1 rounded-lg animate-fade-in"
              style={{ background: voted === 'up' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: voted === 'up' ? 'var(--primary)' : 'var(--danger)' }}>
              {voted === 'up' ? '✓ Upvoted' : '✓ Downvoted'}
            </span>
          )}
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-white/5"
          style={{ color: 'var(--muted-foreground)' }}>
          <Share2 size={14} />
          <span className="text-xs">Share</span>
        </button>
      </div>
    </div>
  );
}

const EMPTY_ROAST: Roast = {
  id: '',
  roasterId: '',
  username: '',
  displayName: '',
  avatarColor: '#22c55e',
  initials: '',
  countryFlag: '',
  country: '',
  text: '',
  category: '',
  votes: 0,
  upvotes: 0,
  downvotes: 0,
  submittedAt: 'Just now',
  tags: [],
};

function AddRoastForm({ label, onAdd, onCancel }: { label: string; onAdd: (r: Roast) => void; onCancel: () => void }) {
  const [form, setForm] = useState({ displayName: '', username: '', country: '', countryFlag: '', text: '', category: '' });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!form.text.trim() || !form.displayName.trim()) return;
    const initials = form.displayName.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    const colors = ['#22c55e', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#f97316', '#a855f7', '#e11d48'];
    const color = colors[Math.floor(Math.random() * colors.length)];
    onAdd({
      ...EMPTY_ROAST,
      id: `roast-${Date.now()}`,
      displayName: form.displayName.trim(),
      username: form.username.trim() || `@${form.displayName.trim().toLowerCase().replace(/\s+/g, '_')}`,
      country: form.country.trim(),
      countryFlag: form.countryFlag.trim(),
      text: form.text.trim(),
      category: form.category.trim() || 'Funniest Roast',
      initials,
      avatarColor: color,
    });
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--border)',
    color: 'var(--foreground)',
    borderRadius: '10px',
    padding: '8px 12px',
    fontSize: '13px',
    width: '100%',
    outline: 'none',
  };

  return (
    <div className="card-glass p-5 rounded-2xl" style={{ border: '1px solid rgba(34,197,94,0.25)' }}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-bold" style={{ color: 'var(--primary)' }}>✏️ Add {label}</span>
        <button onClick={onCancel} className="p-1 rounded-lg hover:bg-white/5" style={{ color: 'var(--muted-foreground)' }}><X size={14} /></button>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input style={inputStyle} placeholder="Display Name *" value={form.displayName} onChange={e => set('displayName', e.target.value)} />
          <input style={inputStyle} placeholder="@username" value={form.username} onChange={e => set('username', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input style={inputStyle} placeholder="Country (e.g. Nigeria)" value={form.country} onChange={e => set('country', e.target.value)} />
          <input style={inputStyle} placeholder="Flag emoji (e.g. 🇳🇬)" value={form.countryFlag} onChange={e => set('countryFlag', e.target.value)} />
        </div>
        <input style={inputStyle} placeholder="Category (e.g. Funniest Roast)" value={form.category} onChange={e => set('category', e.target.value)} />
        <textarea
          style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
          placeholder="Roast text *"
          value={form.text}
          onChange={e => set('text', e.target.value)}
        />
        <button
          onClick={handleSubmit}
          disabled={!form.text.trim() || !form.displayName.trim()}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff' }}
        >
          <Check size={14} />
          Add Entry
        </button>
      </div>
    </div>
  );
}

export default function RoastRankHome() {
  const [roasts, setRoasts] = useState<Roast[]>(ROASTS);
  const [showAddRotd, setShowAddRotd] = useState(false);
  const [showAddTrending, setShowAddTrending] = useState(false);
  const { t } = useLanguage();

  const roastOfDay = roasts.find(r => r.isRoastOfDay) ?? null;
  const trending = roasts.filter(r => r.isTrending);
  const top3 = ROASTERS.slice(0, 3);
  const rest = ROASTERS.slice(3, 10);
  const hasRoasters = ROASTERS.length > 0;

  const addRotd = (r: Roast) => {
    setRoasts(prev => [...prev.map(x => ({ ...x, isRoastOfDay: false })), { ...r, isRoastOfDay: true }]);
    setShowAddRotd(false);
  };

  const addTrending = (r: Roast) => {
    setRoasts(prev => [...prev, { ...r, isTrending: true }]);
    setShowAddTrending(false);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full" style={{ background: 'rgba(10,10,15,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/roastrank" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)' }}>
                <Flame size={18} className="text-white" />
              </div>
              <span className="font-bold text-lg" style={{ color: 'var(--foreground)' }}>Roast<span style={{ color: 'var(--primary)' }}>Rank</span></span>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {[
                { label: t('rr_home'), href: '/roastrank' },
                { label: t('rr_leaderboard'), href: '/roastrank/leaderboard' },
                { label: t('rr_browse'), href: '/roastrank/browse' },
                { label: t('rr_submit'), href: '/roastrank/submit' },
                { label: '🏛️ Legacy', href: '/roastrank/legacy' },
              ].map(item => (
                <Link key={item.href} href={item.href} className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:bg-white/5" style={{ color: 'var(--muted-foreground)' }}>
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              <Link href="/roastrank/submit" className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff' }}>
                <Flame size={14} />
                {t('rr_submit_roast')}
              </Link>
              <RoastRankAuthNav />
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 gradient-hero pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(34,197,94,0.06) 0%, transparent 70%)' }} />
        <div className="max-w-screen-xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-semibold"
            style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', color: 'var(--primary)' }}>
            <span className="pulse-dot" />
            {/* WhatsApp logo SVG */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#25D366' }}>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            🔥 Global WhatsApp roasting competition is live
          </div>
          <h1 className="text-display mb-4" style={{ color: 'var(--foreground)' }}>
            Who's the World's Best<br />
            <span style={{ background: 'linear-gradient(135deg, #22c55e, #4ade80)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Roaster?
            </span>
          </h1>
          <p className="text-lg mb-8 max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            Drop your best roast. Earn votes. Earn points. Climb the rankings. Become legendary.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/roastrank/submit" className="flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 hover:scale-105 hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff', boxShadow: '0 0 30px rgba(34,197,94,0.3)' }}>
              <Flame size={18} />
              Submit Your Roast
            </Link>
            <Link href="/roastrank/leaderboard" className="flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 hover:scale-105"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--foreground)', border: '1px solid var(--border)' }}>
              <Trophy size={18} />
              View Global Rankings
            </Link>
            <Link href="/roastrank/legacy" className="flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 hover:scale-105"
              style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>
              🏛️ Legacy Board
            </Link>
          </div>
          <div className="inline-flex items-center gap-3 mt-8 px-5 py-3 rounded-2xl"
            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
            <ThumbsUp size={16} style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
              Every upvote = <span style={{ color: 'var(--primary)' }}>{POINTS_PER_VOTE} points</span> for the roaster
            </span>
          </div>
        </div>
      </section>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-16">

        {/* Global Top 10 */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
                <Trophy size={16} style={{ color: 'var(--gold)' }} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Global Top 10</h2>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>World's best roasters right now</p>
              </div>
            </div>
            <Link href="/roastrank/leaderboard" className="flex items-center gap-1 text-sm font-semibold transition-colors hover:opacity-80" style={{ color: 'var(--primary)' }}>
              Full Rankings <ChevronRight size={14} />
            </Link>
          </div>

          {!hasRoasters ? (
            <div className="card-glass rounded-2xl p-12 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(245,158,11,0.1)', border: '2px dashed rgba(245,158,11,0.3)' }}>
                <Users size={28} style={{ color: 'var(--gold)' }} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>The throne is empty 👑</h3>
              <p className="text-sm mb-6 max-w-sm mx-auto" style={{ color: 'var(--muted-foreground)' }}>
                No one has claimed the #1 spot yet. Submit a roast, earn upvotes, and be the first on the leaderboard.
              </p>
              <div className="flex items-center justify-center gap-2 mb-6 text-sm font-semibold" style={{ color: 'var(--muted-foreground)' }}>
                <ThumbsUp size={14} style={{ color: 'var(--primary)' }} />
                <span>Each upvote earns the roaster <span style={{ color: 'var(--primary)' }}>{POINTS_PER_VOTE} points</span></span>
              </div>
              <Link href="/roastrank/submit"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff' }}>
                <Flame size={16} />
                Be the First — Submit Your Roast
              </Link>
            </div>
          ) : (
            <>
              {top3.length >= 3 && (
                <div className="flex gap-3 mb-6">
                  {[top3[1], top3[0], top3[2]].map((r, i) => {
                    const position = i === 0 ? 2 : i === 1 ? 1 : 3;
                    const configs = {
                      1: { gradient: 'gradient-podium-gold', ring: 'avatar-ring-gold', label: '🥇', color: 'var(--gold)', size: 64, order: 'order-2' },
                      2: { gradient: 'gradient-podium-silver', ring: 'avatar-ring-silver', label: '🥈', color: 'var(--silver)', size: 56, order: 'order-1' },
                      3: { gradient: 'gradient-podium-bronze', ring: 'avatar-ring-bronze', label: '🥉', color: 'var(--bronze)', size: 56, order: 'order-3' },
                    };
                    const cfg = configs[position as 1 | 2 | 3];
                    const rankChange = r.previousRank - r.globalRank;
                    return (
                      <div key={r.id} className={`${cfg.order} flex flex-col items-center gap-3 p-5 rounded-2xl ${cfg.gradient} transition-all duration-300 hover:scale-105 cursor-pointer flex-1 min-w-0`}>
                        <div className="text-2xl">{cfg.label}</div>
                        <div className={`${cfg.ring} rounded-full`}>
                          <Avatar initials={r.initials} color={r.avatarColor} size={cfg.size} />
                        </div>
                        <div className="text-center min-w-0 w-full">
                          <div className="font-bold text-sm truncate" style={{ color: 'var(--foreground)' }}>{r.displayName}</div>
                          <div className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{r.username}</div>
                          <div className="text-lg mt-1">{r.countryFlag} <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{r.country}</span></div>
                          <div className="font-mono font-bold text-lg mt-1" style={{ color: cfg.color }}>{r.points.toLocaleString()}</div>
                          <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>points</div>
                          {rankChange !== 0 && (
                            <div className="text-xs font-bold mt-1" style={{ color: rankChange > 0 ? 'var(--rise)' : 'var(--fall)' }}>
                              {rankChange > 0 ? `↑ +${rankChange}` : `↓ ${rankChange}`}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="space-y-2">
                {rest.map((roaster) => {
                  const rankChange = roaster.previousRank - roaster.globalRank;
                  return (
                    <div key={roaster.id} className="card-glass flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-white/5 cursor-pointer">
                      <div className="w-12 text-center">
                        <span className="font-mono font-bold text-sm" style={{ color: 'var(--muted-foreground)' }}>#{roaster.globalRank}</span>
                        {rankChange !== 0 && (
                          <div className="text-xs font-bold" style={{ color: rankChange > 0 ? 'var(--rise)' : 'var(--fall)' }}>
                            {rankChange > 0 ? `↑${rankChange}` : `↓${Math.abs(rankChange)}`}
                          </div>
                        )}
                      </div>
                      <Avatar initials={roaster.initials} color={roaster.avatarColor} size={36} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm truncate" style={{ color: 'var(--foreground)' }}>{roaster.displayName}</span>
                          <span className="text-base" title={roaster.country}>{roaster.countryFlag}</span>
                          <span className="text-xs hidden sm:inline" style={{ color: 'var(--muted-foreground)' }}>{roaster.country}</span>
                        </div>
                        <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{roaster.username}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* Roast of the Day + Trending */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Roast of the Day */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.15)' }}>
                  <Star size={16} style={{ color: 'var(--primary)' }} />
                </div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Roast of the Day</h2>
              </div>
              {!showAddRotd && (
                <button
                  onClick={() => setShowAddRotd(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105"
                  style={{ background: 'rgba(34,197,94,0.1)', color: 'var(--primary)', border: '1px solid rgba(34,197,94,0.2)' }}
                >
                  <Plus size={12} />
                  {roastOfDay ? 'Replace' : 'Add Entry'}
                </button>
              )}
            </div>
            {showAddRotd ? (
              <AddRoastForm label="Roast of the Day" onAdd={addRotd} onCancel={() => setShowAddRotd(false)} />
            ) : roastOfDay ? (
              <RoastCard roast={roastOfDay} featured />
            ) : (
              <div className="card-glass rounded-2xl p-10 text-center" style={{ border: '1px dashed rgba(34,197,94,0.2)' }}>
                <div className="text-4xl mb-3">🔥</div>
                <h3 className="font-bold mb-1" style={{ color: 'var(--foreground)' }}>No Roast of the Day yet</h3>
                <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>Click "Add Entry" to feature today's best roast.</p>
                <button
                  onClick={() => setShowAddRotd(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105"
                  style={{ background: 'rgba(34,197,94,0.1)', color: 'var(--primary)', border: '1px solid rgba(34,197,94,0.2)' }}
                >
                  <Plus size={14} />
                  Add Entry
                </button>
              </div>
            )}
          </section>

          {/* Trending Now */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)' }}>
                  <TrendingUp size={16} style={{ color: 'var(--danger)' }} />
                </div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Trending Now</h2>
              </div>
              {!showAddTrending && (
                <button
                  onClick={() => setShowAddTrending(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105"
                  style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.2)' }}
                >
                  <Plus size={12} />
                  Add Entry
                </button>
              )}
            </div>
            {showAddTrending ? (
              <AddRoastForm label="Trending Roast" onAdd={addTrending} onCancel={() => setShowAddTrending(false)} />
            ) : trending.length === 0 ? (
              <div className="card-glass rounded-2xl p-10 text-center" style={{ border: '1px dashed rgba(239,68,68,0.2)' }}>
                <div className="text-4xl mb-3">📈</div>
                <h3 className="font-bold mb-1" style={{ color: 'var(--foreground)' }}>Nothing trending yet</h3>
                <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>Click "Add Entry" to add trending roasts manually.</p>
                <button
                  onClick={() => setShowAddTrending(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105"
                  style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.2)' }}
                >
                  <Plus size={14} />
                  Add Entry
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {trending.map(roast => (
                  <RoastCard key={roast.id} roast={roast} />
                ))}
                <button
                  onClick={() => setShowAddTrending(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 hover:bg-white/5"
                  style={{ border: '1px dashed rgba(239,68,68,0.2)', color: 'var(--muted-foreground)' }}
                >
                  <Plus size={14} />
                  Add Another
                </button>
              </div>
            )}
          </section>
        </div>

        {/* Categories */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.15)' }}>
              <Zap size={16} style={{ color: '#8b5cf6' }} />
            </div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Browse Categories</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map(cat => (
              <div key={cat.id} className="card-glass p-4 rounded-xl text-center cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-white/5">
                <div className="text-2xl mb-2">{cat.icon}</div>
                <div className="text-xs font-semibold leading-tight" style={{ color: 'var(--foreground)' }}>{cat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Country Rankings */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(6,182,212,0.15)' }}>
                <Globe size={16} style={{ color: '#06b6d4' }} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Country Rankings</h2>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Top roasting nations</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {COUNTRY_RANKINGS.slice(0, 8).map(c => (
              <div key={c.countryCode} className="card-glass p-4 rounded-xl transition-all duration-200 hover:bg-white/5 cursor-pointer">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{c.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate" style={{ color: 'var(--foreground)' }}>{c.country}</div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>#{c.rank} globally</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="font-mono font-bold text-sm" style={{ color: 'var(--primary)' }}>{c.totalRoasters.toLocaleString()}</div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>roasters</div>
                  </div>
                  <div>
                    <div className="font-mono font-bold text-sm" style={{ color: 'var(--foreground)' }}>{c.wins.toLocaleString()}</div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>wins</div>
                  </div>
                </div>
                <div className="mt-2 text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                  Top: <span style={{ color: 'var(--primary)' }}>{c.topRoaster}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Weekly Competition */}
        {WEEKLY_LEADERS.length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
                <Crown size={16} style={{ color: 'var(--gold)' }} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Weekly Competition</h2>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Resets every Monday · 3 days left</p>
              </div>
            </div>
            <div className="card-glass rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(245,158,11,0.2)' }}>
              <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                {WEEKLY_LEADERS.map((r, i) => (
                  <div key={r.id} className="flex items-center gap-3 px-4 py-3 transition-all duration-200 hover:bg-white/5">
                    <div className="w-6 text-center font-mono font-bold text-sm" style={{ color: i < 3 ? 'var(--gold)' : 'var(--muted-foreground)' }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                    </div>
                    <Avatar initials={r.initials} color={r.avatarColor} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate" style={{ color: 'var(--foreground)' }}>{r.displayName}</div>
                      <div className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                        <span>{r.countryFlag}</span>
                        <span>{r.country}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-sm" style={{ color: 'var(--gold)' }}>{r.weeklyPoints.toLocaleString()}</div>
                      <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>this week</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="text-center py-12 rounded-3xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(22,163,74,0.1), rgba(34,197,94,0.05))', border: '1px solid rgba(34,197,94,0.2)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(34,197,94,0.08) 0%, transparent 70%)' }} />
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-3" style={{ color: 'var(--foreground)' }}>Ready to Roast?</h2>
            <p className="mb-2" style={{ color: 'var(--muted-foreground)' }}>Submit your best roast. Every upvote earns you points. Climb the ranks.</p>
            <p className="text-sm mb-6 font-semibold" style={{ color: 'var(--primary)' }}>👆 {POINTS_PER_VOTE} points per upvote · The more votes, the higher you rank</p>
            <Link href="/roastrank/submit" className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff', boxShadow: '0 0 30px rgba(34,197,94,0.3)' }}>
              <Flame size={18} />
              Submit Your Roast Now
            </Link>
          </div>
        </section>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden z-50" style={{ background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(20px)', borderTop: '1px solid var(--border)' }}>
        <div className="flex items-center justify-around py-2">
          {[
            { label: 'Home', href: '/roastrank', icon: '🏠' },
            { label: 'Rankings', href: '/roastrank/leaderboard', icon: '🏆' },
            { label: 'Submit', href: '/roastrank/submit', icon: '🔥' },
          ].map(item => (
            <Link key={item.href} href={item.href} className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-200">
              <span className="text-xl">{item.icon}</span>
              <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
